import { useQuery } from '@tanstack/react-query'
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  Leaf,
  MapPin,
  Menu as MenuIcon,
  ShieldCheck,
} from 'lucide-react'
import { type MouseEvent, useEffect } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { getStorefront } from '@/api/storefront'
import { getPlans } from '@/api/subscriptions'
import { customerAvailabilityCopy } from '@/lib/customerAvailability'
import { deliveryPlaces } from '@/lib/deliveryPlaces'
import { shareSource } from '@/lib/shareAttribution'

import { BrandMark } from '../components/BrandMark'
import { Loading } from '../components/Loading'
import { Money } from '../components/Money'
import { Badge } from '../components/ui/Badge'
import { IconButton } from '../components/ui/Button'
import { FadeImg } from '../components/ui/FadeImg'
import { EmptyState, ErrorState } from '../components/ui/States'
import { useKitchenStore } from '../kitchenStore'

/* Hand the tapped card's photo to the route view transition so it morphs
   into the product page photo (the name is claimed just-in-time — the list
   can only ever carry one). */
function morphPhotoIntoProduct(event: MouseEvent<HTMLAnchorElement>) {
  event.currentTarget
    .querySelector('img')
    ?.style.setProperty('view-transition-name', 'product-photo')
}

function displayDate(value: string): string {
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${value}T00:00:00`))
}

function campaignDateLabel(value: string): string {
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  const iso = (date: Date) =>
    [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-')
  if (value === iso(today)) return 'Today'
  if (value === iso(tomorrow)) return 'Tomorrow'
  return displayDate(value)
}

function campaignServiceLabel(serviceDate: string, serviceLabel: string) {
  const dateLabel = campaignDateLabel(serviceDate)
  return ['Today', 'Tomorrow'].includes(dateLabel)
    ? `${dateLabel}’s ${serviceLabel}`
    : `${serviceLabel} on ${dateLabel}`
}

function displayTime(value: string): string {
  const [hours = '0', minutes = '0'] = value.split(':')
  const date = new Date(2026, 0, 1, Number(hours), Number(minutes))
  return new Intl.DateTimeFormat('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

type ServiceMenu = Awaited<ReturnType<typeof getStorefront>>['menus'][number]

function ServiceMenuSection({
  menu,
  slug,
  serviceDate,
  serviceKey,
  spotlightProductId,
  attributionSource,
  subscriptionFulfilmentId,
}: {
  menu: ServiceMenu
  slug: string
  serviceDate?: string
  serviceKey?: string
  spotlightProductId?: string
  attributionSource?: string
  subscriptionFulfilmentId?: string
}) {
  const sections = new Map<string, Map<string, typeof menu.items>>()
  for (const item of menu.items) {
    const section = sections.get(item.section_name) ?? new Map()
    const productItems = section.get(item.product_id) ?? []
    productItems.push(item)
    section.set(item.product_id, productItems)
    sections.set(item.section_name, section)
  }
  const available = menu.items.reduce(
    (total, item) => total + item.available_quantity - item.sold_quantity,
    0,
  )
  const availabilityCopy = customerAvailabilityCopy(
    menu.quantity_limit_enabled,
    available,
    10,
  )
  return (
    <section
      id={`service-${menu.service_key}`}
      className="grid gap-4 rounded-card border border-line/70 bg-surface-tint/40 p-3"
    >
      <div className="flex items-end justify-between gap-3 px-1">
        <div className="grid gap-0.5">
          <span className="text-sm font-semibold text-brand">
            {displayTime(menu.delivery_start)}–{displayTime(menu.delivery_end)}
          </span>
          <h3 className="font-display text-title text-ink">
            {menu.service_label}
          </h3>
        </div>
        <Badge
          tone={
            availabilityCopy === 'Made fresh to order' ? 'neutral' : 'warning'
          }
          dot
        >
          {availabilityCopy}
        </Badge>
      </div>
      {[...sections.entries()]
        .sort(([, left], [, right]) => {
          const leftHasSpotlight = [...left.values()].some((items) =>
            items.some((item) => item.product_id === spotlightProductId),
          )
          const rightHasSpotlight = [...right.values()].some((items) =>
            items.some((item) => item.product_id === spotlightProductId),
          )
          return Number(rightHasSpotlight) - Number(leftHasSpotlight)
        })
        .map(([section, products], sectionIndex) => (
          <div key={section} className="grid gap-3">
            {(sections.size > 1 ||
              !['Menu', menu.service_label].includes(section)) && (
              <h4 className="px-1 text-sm font-bold text-ink-soft">
                {section}
              </h4>
            )}
            <div className="stagger grid gap-4 sm:grid-cols-2">
              {[...products.values()]
                .sort((left, right) =>
                  left[0]?.product_id === spotlightProductId
                    ? -1
                    : right[0]?.product_id === spotlightProductId
                      ? 1
                      : 0,
                )
                .map((options, productIndex) => {
                  const item = options[0]
                  const left = options.reduce(
                    (total, option) =>
                      total + option.available_quantity - option.sold_quantity,
                    0,
                  )
                  const lowestPrice = Math.min(
                    ...options.map((option) => option.price_paise),
                  )
                  const itemAvailabilityCopy = customerAvailabilityCopy(
                    menu.quantity_limit_enabled,
                    left,
                    5,
                  )
                  const itemTo = {
                    pathname: `/k/${slug}/items/${item.id}`,
                    search: new URLSearchParams({
                      ...(serviceDate ? { service_date: serviceDate } : {}),
                      ...(serviceKey ? { service_key: serviceKey } : {}),
                      ...(attributionSource
                        ? { source: attributionSource }
                        : {}),
                      ...(subscriptionFulfilmentId
                        ? { add_to: subscriptionFulfilmentId }
                        : {}),
                    }).toString(),
                  }
                  const flag =
                    item.product_id === spotlightProductId
                      ? 'Fresh spotlight'
                      : itemAvailabilityCopy !== 'Made fresh to order'
                        ? itemAvailabilityCopy
                        : null
                  /* The service's lead dish runs as a marquee card: taller
                     photo, name over its own pine scrim — legible whatever
                     photography the kitchen uploads. */
                  if (
                    sectionIndex === 0 &&
                    productIndex === 0 &&
                    item.image_url
                  ) {
                    return (
                      <Link
                        key={item.product_id}
                        viewTransition
                        onClick={morphPhotoIntoProduct}
                        className="menu-row group relative overflow-hidden rounded-card shadow-card transition duration-300 hover:-translate-y-0.5 hover:shadow-raised active:scale-[0.99] motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100 sm:col-span-2"
                        to={itemTo}
                      >
                        <FadeImg
                          src={item.image_url}
                          loading="lazy"
                          className="aspect-[4/3] w-full sm:aspect-[16/9]"
                          imgClassName="transition duration-700 group-hover:scale-[1.04] motion-reduce:group-hover:scale-100"
                        />
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-pine/90 via-pine/30 to-pine/5"
                        />
                        {flag && (
                          <span className="absolute top-3 left-3 rounded-full bg-pine/80 px-2.5 py-1 text-xs font-bold text-white">
                            {flag}
                          </span>
                        )}
                        <span className="absolute inset-x-0 bottom-0 grid gap-1 p-4">
                          <span className="flex items-end justify-between gap-3">
                            <span className="min-w-0 font-display text-title text-white text-shadow-md">
                              {options.length > 1
                                ? item.name
                                : item.display_name}
                            </span>
                            <span className="mb-1 shrink-0 rounded-full bg-surface/95 px-3 py-1.5 text-sm font-bold text-ink tabular-nums shadow-card">
                              {options.length > 1 && 'From '}
                              <Money paise={lowestPrice} />
                            </span>
                          </span>
                          <span className="line-clamp-1 text-sm text-white/85 text-shadow-sm">
                            {item.today_note || item.description}
                          </span>
                        </span>
                      </Link>
                    )
                  }
                  return (
                    <Link
                      key={item.product_id}
                      viewTransition
                      onClick={morphPhotoIntoProduct}
                      className="menu-row group overflow-hidden rounded-card border border-line/70 bg-surface shadow-card transition duration-300 hover:-translate-y-0.5 hover:border-line-strong hover:shadow-raised active:scale-[0.99] motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100"
                      to={itemTo}
                    >
                      <div className="relative">
                        {item.image_url ? (
                          <FadeImg
                            src={item.image_url}
                            loading="lazy"
                            className="aspect-[16/10] w-full"
                            imgClassName="transition duration-500 group-hover:scale-[1.03] motion-reduce:group-hover:scale-100"
                          />
                        ) : (
                          <span className="grid aspect-[16/10] w-full place-items-center bg-surface-tint text-brand">
                            <Leaf className="size-8" aria-hidden="true" />
                          </span>
                        )}
                        {flag && (
                          <span className="absolute top-3 left-3 rounded-full bg-pine/80 px-2.5 py-1 text-xs font-bold text-white">
                            {flag}
                          </span>
                        )}
                        <span className="absolute right-3 bottom-3 rounded-full bg-surface/95 px-3 py-1.5 text-sm font-bold text-ink tabular-nums shadow-card">
                          {options.length > 1 && 'From '}
                          <Money paise={lowestPrice} />
                        </span>
                      </div>
                      <div className="grid gap-1 px-4 pt-3 pb-3.5">
                        <span className="flex items-center justify-between gap-3">
                          <h4 className="min-w-0 font-display text-title-sm text-ink">
                            {options.length > 1 ? item.name : item.display_name}
                          </h4>
                          <ArrowRight
                            aria-hidden="true"
                            className="size-4 shrink-0 text-ink-faint transition duration-300 ease-spring group-hover:translate-x-0.5 group-hover:text-brand motion-reduce:group-hover:translate-x-0"
                          />
                        </span>
                        {options.length > 1 && (
                          <Badge tone="accent" className="justify-self-start">
                            {options
                              .map((option) => option.variant_name)
                              .join(' · ')}
                          </Badge>
                        )}
                        <p className="line-clamp-2 text-sm text-ink-soft">
                          {item.today_note || item.description}
                        </p>
                        {options.length === 1 && item.contents.length > 0 && (
                          <span className="line-clamp-1 text-sm font-semibold text-brand">
                            {item.contents.join(' · ')}
                          </span>
                        )}
                      </div>
                    </Link>
                  )
                })}
            </div>
          </div>
        ))}
    </section>
  )
}

export function StorefrontPage() {
  const {
    slug = 'morning-bowl-co',
    serviceDate: campaignDate,
    serviceKey: campaignServiceKey,
  } = useParams()
  const [searchParams] = useSearchParams()
  const serviceDate =
    campaignDate ?? searchParams.get('service_date') ?? undefined
  const serviceKey =
    campaignServiceKey ?? searchParams.get('service_key') ?? undefined
  const attributionSource = shareSource(searchParams.get('source'))
  const spotlightProductId = searchParams.get('spotlight') ?? undefined
  const subscriptionFulfilmentId = searchParams.get('add_to') ?? undefined
  const setKitchen = useKitchenStore((state) => state.setKitchen)
  const query = useQuery({
    queryKey: ['storefront', slug, serviceDate, serviceKey],
    queryFn: () => getStorefront(slug, serviceDate, serviceKey),
  })
  const plans = useQuery({
    queryKey: ['plans', slug],
    queryFn: () => getPlans(slug),
  })

  const firstItem =
    query.data?.menus
      .flatMap((menu) => menu.items)
      .find((item) => item.product_id === spotlightProductId) ??
    query.data?.menus.flatMap((menu) => menu.items)[0]
  useEffect(() => {
    if (query.data) {
      setKitchen({
        slug,
        name: query.data.kitchen.name,
        brandPrimary: query.data.kitchen.brand_primary,
      })
    }
  }, [query.data, setKitchen, slug])

  if (query.isPending) return <Loading />
  if (query.isError || !query.data) {
    return (
      <div className="grid flex-1 content-center p-5">
        <ErrorState
          title="We couldn’t open this kitchen"
          body={query.error?.message ?? 'Please try again.'}
          onRetry={() => void query.refetch()}
        />
      </div>
    )
  }

  const { kitchen, menus, service_areas: areas } = query.data
  const primaryMenu = menus[0]
  const places = deliveryPlaces(areas)
  /* Appetite first: if the kitchen has no hero and the lead item has no
     photo, fall back to the first dish that does have one. */
  const heroImage =
    kitchen.hero_image_url ??
    firstItem?.image_url ??
    menus.flatMap((menu) => menu.items).find((item) => item.image_url)
      ?.image_url ??
    null
  const isCampaign = Boolean(campaignDate && campaignServiceKey)

  return (
    <div className="flex flex-col">
      <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-line/70 bg-canvas/90 px-5 pt-[max(0.75rem,var(--safe-top))] pb-3 backdrop-blur-md">
        <BrandMark name={kitchen.name} />
        <IconButton
          label="View today's menu"
          variant="soft"
          onClick={() =>
            document
              .querySelector('#today-menu')
              ?.scrollIntoView({ behavior: 'smooth' })
          }
        >
          <MenuIcon className="size-5" />
        </IconButton>
      </header>

      {primaryMenu ? (
        <div className="flex flex-col pb-10">
          {/* Full-bleed hero: appetite first, headline over the image. */}
          <section className="relative overflow-hidden">
            {heroImage ? (
              <div className="hero-parallax">
                <FadeImg
                  src={heroImage}
                  alt={firstItem?.name ?? kitchen.name}
                  fetchPriority="high"
                  className="aspect-[4/3] w-full bg-pine"
                  imgClassName="animate-hero-drift motion-reduce:animate-none"
                />
              </div>
            ) : (
              <div
                aria-hidden="true"
                className="aspect-[16/10] w-full bg-gradient-to-br from-brand via-brand-strong to-brand-pressed"
              />
            )}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-t from-pine/90 via-pine/50 to-pine/15"
            />
            <div className="absolute inset-x-0 bottom-0 grid gap-2.5 p-5 pb-13">
              <span className="inline-flex w-fit items-center rounded-full bg-pine/55 px-3.5 py-1 font-display text-[0.95rem] text-accent italic">
                {isCampaign
                  ? spotlightProductId
                    ? campaignServiceLabel(
                        primaryMenu.service_date,
                        primaryMenu.service_label,
                      )
                    : kitchen.name
                  : 'Fresh for your day'}
              </span>
              <h1 className="max-w-[16ch] font-display text-display text-white text-shadow-md">
                {isCampaign
                  ? spotlightProductId && firstItem
                    ? firstItem.display_name
                    : campaignServiceLabel(
                        primaryMenu.service_date,
                        primaryMenu.service_label,
                      )
                  : 'Fresh food, made for your day.'}
              </h1>
              {(spotlightProductId && firstItem
                ? firstItem.today_note || firstItem.description
                : kitchen.public_summary) && (
                <p className="line-clamp-2 max-w-prose text-sm text-white/90 text-shadow-sm">
                  {spotlightProductId && firstItem
                    ? firstItem.today_note || firstItem.description
                    : kitchen.public_summary}
                </p>
              )}
              <a
                href="#today-menu"
                className="group mt-1.5 inline-flex min-h-12 w-fit items-center gap-3 rounded-full bg-gradient-to-b from-brand to-brand-strong py-1.5 pr-2 pl-6 font-sans text-[0.95rem] font-semibold text-on-brand shadow-button transition duration-200 hover:-translate-y-px hover:from-brand-strong hover:to-brand-pressed hover:shadow-button-up active:translate-y-0 active:scale-[0.98] motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100"
              >
                {isCampaign
                  ? spotlightProductId
                    ? 'Choose this dish'
                    : 'Choose yours'
                  : 'Browse the menu'}
                <span className="grid size-9 place-items-center rounded-full bg-white/15 transition duration-300 ease-spring group-hover:translate-x-0.5 group-hover:bg-white/25">
                  <ArrowRight className="size-4" aria-hidden="true" />
                </span>
              </a>
            </div>
          </section>

          {/* Delivery window floats over the hero's lower edge. */}
          <div className="relative z-10 -mt-7 px-5">
            <section
              aria-label="Delivery window"
              className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-card border border-line/60 bg-surface p-4 shadow-raised"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-tint text-brand">
                <Clock3 className="size-5" aria-hidden="true" />
              </span>
              <div className="grid min-w-0 flex-1 gap-0">
                <span className="truncate text-sm text-ink-soft">
                  {displayDate(primaryMenu.service_date)}
                </span>
                <strong className="text-base font-bold whitespace-nowrap text-ink">
                  {displayTime(primaryMenu.delivery_start)} –{' '}
                  {displayTime(primaryMenu.delivery_end)}
                </strong>
                <small className="text-xs font-semibold text-brand">
                  Orders close{' '}
                  {new Intl.DateTimeFormat('en-IN', {
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZone: kitchen.timezone,
                  }).format(new Date(primaryMenu.order_cutoff_at))}
                </small>
              </div>
              <Badge tone="brand" className="col-start-2 justify-self-start">
                {menus.length > 1
                  ? `${menus.length} services available`
                  : primaryMenu.service_label}
              </Badge>
            </section>
          </div>

          <div className="stagger flex flex-col gap-8 px-5 pt-7">
            {query.data.campaign_notice && (
              <section
                role="status"
                className="flex items-start gap-3 rounded-card bg-accent-tint p-4 text-accent-strong"
              >
                <CheckCircle2
                  className="mt-0.5 size-5 shrink-0"
                  aria-hidden="true"
                />
                <strong className="text-sm font-bold">
                  {query.data.campaign_notice}
                </strong>
              </section>
            )}
            {subscriptionFulfilmentId && (
              <section className="flex items-start gap-3 rounded-card bg-brand-tint p-4">
                <CheckCircle2
                  className="mt-0.5 size-5 shrink-0 text-brand"
                  aria-hidden="true"
                />
                <div className="grid gap-0.5">
                  <strong className="font-bold text-ink">
                    Add to your plan delivery
                  </strong>
                  <small className="text-sm text-ink-soft">
                    Choose an extra item for{' '}
                    {displayDate(primaryMenu.service_date)}. Your plan address
                    is reused and no second delivery fee is charged.
                  </small>
                </div>
              </section>
            )}

            <section
              aria-labelledby="delivery-areas-heading"
              className="grid gap-4 rounded-card border border-line/70 bg-surface p-4 shadow-card"
            >
              <div className="flex items-center gap-3">
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-tint text-brand">
                  <MapPin className="size-5" aria-hidden="true" />
                </span>
                <div className="grid min-w-0 gap-0">
                  <span className="text-sm text-ink-soft">
                    Fresh delivery around
                  </span>
                  <h2
                    id="delivery-areas-heading"
                    className="font-sans text-base font-bold text-ink"
                  >
                    Neighbourhoods we serve
                  </h2>
                </div>
              </div>
              {places.length ? (
                <ul
                  aria-label="Delivery neighbourhoods"
                  className="flex flex-wrap gap-2"
                >
                  {places.map((place) => (
                    <li
                      key={place.toLocaleLowerCase('en-IN')}
                      className="rounded-full bg-brand-tint px-3 py-2 text-sm font-bold text-brand-on-tint"
                    >
                      {place}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-ink-soft">
                  Delivery neighbourhoods are being added.
                </p>
              )}
              <p className="border-t border-line/70 pt-3 text-sm text-ink-soft">
                Add your address at checkout. We’ll confirm the delivery PIN and
                fee before you place the order.
              </p>
            </section>

            <section className="grid grid-cols-1 gap-4" id="today-menu">
              <div className="flex items-end justify-between gap-3">
                <div className="grid gap-0.5">
                  <span className="font-display text-base text-brand italic">
                    Made for this service
                  </span>
                  <h2 className="font-display text-title text-ink">
                    {subscriptionFulfilmentId
                      ? 'Add-on menu'
                      : 'Available menu'}
                  </h2>
                </div>
                <Badge tone="neutral" dot>
                  Made fresh to order
                </Badge>
              </div>
              {menus.length > 1 && (
                <nav
                  aria-label="Available services"
                  className="snap-rail -mx-5 flex gap-2 overflow-x-auto px-5 pb-1"
                >
                  {menus.map((menu) => (
                    <a
                      key={menu.id}
                      href={`#service-${menu.service_key}`}
                      className="shrink-0 rounded-full border border-line-strong bg-surface px-4 py-2 text-sm font-bold text-brand shadow-[0_1px_2px_rgb(18_41_28/6%)] transition hover:border-brand hover:bg-brand-tint active:scale-[0.97] motion-reduce:active:scale-100"
                    >
                      {menu.service_label}
                    </a>
                  ))}
                </nav>
              )}
              <div className="grid gap-5">
                {menus.map((menu) => (
                  <ServiceMenuSection
                    key={menu.id}
                    menu={menu}
                    slug={slug}
                    serviceDate={menu.service_date}
                    serviceKey={menu.service_key}
                    spotlightProductId={spotlightProductId}
                    attributionSource={attributionSource}
                    subscriptionFulfilmentId={subscriptionFulfilmentId}
                  />
                ))}
              </div>
            </section>

            <section className="flex items-center gap-3 px-1">
              <ShieldCheck
                className="size-5 shrink-0 text-success"
                aria-hidden="true"
              />
              <p className="text-sm text-ink-soft">
                Ingredients and allergens are shown for every menu item.
              </p>
            </section>

            {plans.data?.length ? (
              <section className="grid gap-4" id="plans">
                <div className="grid gap-0.5">
                  <span className="font-display text-base text-brand italic">
                    Make repeat orders easier
                  </span>
                  <h2 className="font-display text-title text-ink">
                    Meal plans
                  </h2>
                </div>
                <div className="grid gap-3">
                  {plans.data.map((plan) => (
                    <Link
                      key={plan.id}
                      viewTransition
                      to={`/k/${slug}/plans/${plan.id}`}
                      className="group relative flex items-center gap-4 overflow-hidden rounded-card bg-pine p-5 shadow-raised ring-1 ring-white/10 transition duration-300 ring-inset hover:-translate-y-0.5 hover:shadow-overlay active:scale-[0.99] motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100"
                    >
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -top-12 -right-8 size-40 rounded-full bg-brand/40 blur-2xl"
                      />
                      <div className="relative grid min-w-0 flex-1 gap-1">
                        <span className="font-display text-sm text-accent italic">
                          {plan.cadence}
                        </span>
                        <h3 className="font-display text-title-sm text-white">
                          {plan.name}
                        </h3>
                        <p className="text-sm text-white/70">
                          {plan.included_deliveries} deliveries ·{' '}
                          {plan.duration_days} days ·{' '}
                          <Money
                            paise={Math.round(
                              plan.price_paise / plan.included_deliveries,
                            )}
                          />
                          /delivery
                        </p>
                      </div>
                      <strong className="relative text-lg font-bold text-white tabular-nums">
                        <Money paise={plan.price_paise} />
                      </strong>
                      <span className="relative grid size-8 shrink-0 place-items-center rounded-full bg-white/10 text-white transition duration-300 ease-spring group-hover:bg-accent group-hover:text-on-accent">
                        <ArrowRight className="size-4" aria-hidden="true" />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="px-5 pt-6 pb-10">
          <EmptyState
            title="The next menu is being prepared"
            body={
              query.data.campaign_notice ??
              'Check back soon for the kitchen’s next delivery date.'
            }
          />
        </div>
      )}
      <footer className="mt-2 grid justify-items-center gap-2 border-t border-line px-5 py-9 text-center">
        <span
          aria-hidden="true"
          className="grid size-9 place-items-center rounded-full bg-brand-tint text-brand"
        >
          <Leaf className="size-4" />
        </span>
        <p className="font-display text-lg text-ink italic">{kitchen.name}</p>
        <span className="text-sm text-ink-soft">
          {places.length
            ? `Made fresh for each service · delivery around ${places[0]}`
            : 'Delivery areas coming soon'}
        </span>
      </footer>
    </div>
  )
}
